import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/layouts/RootLayout';
import { useFunction } from '@/hooks/useFunction';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Busy, ErrorState, Field, PageHeader, Panel } from '@/components/kit';
import { PickSelect } from '@/components/inputs';
import { ListSkeleton } from '@/components/PageSkeleton';
import { logActivity } from '@/shared/data';
import { cn } from '@/lib/utils';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/email', layout: 'owner', access: "profile === 'Admin_c'" };
export const nav = { icon: 'Mail', label: 'Email settings', section: 'Store', profiles: ['Admin_c'], order: 44 };

const SEND_EMAIL = import.meta.env.VITE_SEND_EMAIL;

const PRESETS = [
  {
    id: 'gmail',
    label: 'Gmail',
    host: 'smtp.gmail.com',
    hint: 'Turn on 2-Step Verification on the Google account, then create an App Password (Google Account → Security → App passwords) and paste the 16 letters as the password. Your normal Gmail password will not work.',
  },
  {
    id: 'outlook',
    label: 'Outlook / Microsoft 365',
    host: 'smtp.office365.com',
    hint: 'Use the full email address as the username. If the account has two-step sign-in, create an app password. Microsoft 365 work accounts need SMTP AUTH turned on by the admin.',
  },
  {
    id: 'yahoo',
    label: 'Yahoo',
    host: 'smtp.mail.yahoo.com',
    hint: 'Generate an app password under Yahoo Account Security and use it as the password.',
  },
  {
    id: 'zoho',
    label: 'Zoho',
    host: 'smtp.zoho.com',
    hint: 'Use the full email address. Accounts with two-factor sign-in need an application-specific password.',
  },
];

const SECURITY_LABELS = { starttls: 'STARTTLS (port 587)', ssl: 'SSL/TLS (port 465)' };

const EMPTY = {
  host: '',
  port: '587',
  security: 'starttls',
  username: '',
  password: '',
  fromEmail: '',
  fromName: 'Ghost Cable',
  replyTo: '',
  ownerEmail: '',
};

function formFrom(s) {
  if (!s) return EMPTY;
  return {
    host: s.smtp?.host ?? '',
    port: String(s.smtp?.port || 587),
    security: s.smtp?.security === 'ssl' ? 'ssl' : 'starttls',
    username: s.smtp?.username ?? '',
    password: '',
    fromEmail: s.fromEmail ?? '',
    fromName: s.fromName || 'Ghost Cable',
    replyTo: s.replyTo ?? '',
    ownerEmail: s.ownerEmail ?? '',
  };
}

function StatusLine({ status }) {
  if (!status) return null;
  const s = status.settings ?? {};
  const ok = status.configured;
  const via = `SMTP · ${s.smtp?.host || 'no server'}`;
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3',
        ok ? 'border-success-border bg-success-muted' : 'border-warning-border bg-warning-muted',
      )}
      role="status"
    >
      <ApperIcon
        name={ok ? 'MailCheck' : 'MailWarning'}
        size={22}
        className={cn('mt-0.5 shrink-0', ok ? 'text-success' : 'text-warning')}
      />
      <div className="min-w-0 text-sm">
        <p className="font-medium text-foreground">{ok ? `Email is on · ${via}` : 'Email is not sending yet'}</p>
        <p className="mt-0.5 text-muted-foreground">
          {ok
            ? `Order confirmations, verifier results, broadcasts, reminders and order messages go out from ${
                s.fromEmail || s.smtp?.username || 'your From address'
              }.`
            : status.problem || 'Fill in the settings below and save.'}
        </p>
        {s.updatedAt && (
          <p className="mt-1 text-xs text-muted-foreground" title={formatLocalDate(s.updatedAt, DATE_FORMATS.WITH_TIME)}>
            Saved {formatRelativeTime(s.updatedAt)}
          </p>
        )}
      </div>
    </div>
  );
}

function TestPanel({ configured }) {
  const { user } = useAuth();
  const [to, setTo] = useState(user?.emailAddress ?? '');
  const tester = useFunction(SEND_EMAIL);

  async function send(e) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) {
      toast.error('Enter an email address to send the test to');
      return;
    }
    const res = await tester.invoke({ mode: 'test', to: to.trim() });
    if (!res) return;
    if (res.partial || !res.sent) {
      toast.error(res.message || 'The test did not go out');
      return;
    }
    toast.success(`Test sent to ${to.trim()}`, { description: 'Check the inbox and the spam folder.' });
  }

  return (
    <Panel title="Send a test" icon="Send" bodyClassName="py-4">
      <form onSubmit={send} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field label="Send to" htmlFor="test-to" className="min-w-0 flex-1">
          <Input
            id="test-to"
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />
        </Field>
        <Button type="submit" disabled={tester.loading || !configured} className="sm:w-40">
          <Busy
            busy={tester.loading}
            label={
              <>
                <ApperIcon name="Send" size={16} />
                Send test
              </>
            }
            busyLabel="Sending…"
          />
        </Button>
      </form>
      {!configured && <p className="mt-2 text-xs text-muted-foreground">Save working settings first.</p>}
    </Panel>
  );
}

export default function EmailSettings() {
  const loader = useFunction(SEND_EMAIL, { showError: false });
  const saver = useFunction(SEND_EMAIL);
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [warning, setWarning] = useState(null);

  async function load() {
    setLoadError(null);
    const res = await loader.invoke({ mode: 'settings-get' });
    if (!res) {
      setLoadError('Could not read the email settings.');
      return;
    }
    setStatus(res);
    setForm(formFrom(res.settings));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));
  const s = status?.settings;
  const preset = PRESETS.find((p) => p.host === form.host.trim().toLowerCase());
  const hasPassword = !!s?.smtp?.hasPassword && s?.smtp?.host === form.host.trim().toLowerCase() && s?.smtp?.username === form.username.trim();

  function applyPreset(p) {
    setForm((f) => ({ ...f, host: p.host, port: '587', security: 'starttls' }));
  }

  function setSecurity(v) {
    setForm((f) => ({ ...f, security: v, port: v === 'ssl' ? '465' : f.port === '465' ? '587' : f.port }));
  }

  async function save(e) {
    e.preventDefault();
    setWarning(null);
    if (!form.host.trim() || !form.username.trim()) {
      toast.error('Add the SMTP server and username');
      return;
    }
    if (!form.password.trim() && !hasPassword) {
      toast.error('Add the password or app password');
      return;
    }
    const res = await saver.invoke({
      mode: 'settings-save',
      smtp: {
        host: form.host.trim(),
        port: Number(form.port) || 587,
        security: form.security,
        username: form.username.trim(),
        password: form.password,
      },
      fromEmail: form.fromEmail.trim(),
      fromName: form.fromName.trim(),
      replyTo: form.replyTo.trim(),
      ownerEmail: form.ownerEmail.trim(),
    });
    if (!res) return;
    logActivity('Email', 'Updated email settings', form.host.trim());
    if (res.verified === false && res.warning) {
      setWarning(res.warning);
      toast.warning('Saved, but the sign-in check failed', { description: res.warning });
    } else {
      toast.success('Saved. The SMTP sign-in works.');
    }
    await load();
  }

  if (loadError && !status) {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader eyebrow="Store" title="Email settings" />
        <ErrorState message={loadError} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="Store"
        title="Email settings"
        description="Send the store's emails through your own mailbox: Gmail, Outlook or any SMTP server."
      />

      {!status ? (
        <ListSkeleton rows={4} />
      ) : (
        <div className="space-y-6">
          <StatusLine status={status} />

          <form onSubmit={save} className="space-y-6">
            <Panel title="How email goes out" icon="ServerCog" bodyClassName="space-y-4 py-4">
              {(
                <>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Quick fill</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {PRESETS.map((p) => (
                        <Button
                          key={p.id}
                          type="button"
                          size="sm"
                          variant={preset?.id === p.id ? 'default' : 'outline'}
                          onClick={() => applyPreset(p)}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>
                    {preset && (
                      <p className="mt-2 flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                        <ApperIcon name="Info" size={14} className="mt-0.5 shrink-0 text-primary" />
                        {preset.hint}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_7rem]">
                    <Field label="SMTP server" htmlFor="smtp-host">
                      <Input
                        id="smtp-host"
                        value={form.host}
                        onChange={set('host')}
                        placeholder="smtp.gmail.com"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </Field>
                    <Field label="Port" htmlFor="smtp-port">
                      <Input
                        id="smtp-port"
                        inputMode="numeric"
                        value={form.port}
                        onChange={(e) => set('port')(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        placeholder="587"
                      />
                    </Field>
                  </div>
                  <Field
                    label="Security"
                    htmlFor="smtp-security"
                    hint="Use STARTTLS on port 587. Port 465 is blocked from our mail server, so SSL/TLS on 465 will not connect."
                  >
                    <PickSelect
                      id="smtp-security"
                      value={form.security}
                      onChange={setSecurity}
                      values={['starttls', 'ssl']}
                      labels={SECURITY_LABELS}
                    />
                  </Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Username" htmlFor="smtp-user" hint="Usually the full email address.">
                      <Input
                        id="smtp-user"
                        value={form.username}
                        onChange={set('username')}
                        placeholder="admin.ghost.cable@gmail.com"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </Field>
                    <Field
                      label="Password or app password"
                      htmlFor="smtp-pass"
                      hint={hasPassword ? 'A password is saved. Leave blank to keep it.' : 'Stored encrypted. Never shown again.'}
                    >
                      <Input
                        id="smtp-pass"
                        type="password"
                        value={form.password}
                        onChange={set('password')}
                        placeholder={hasPassword ? '•••••••• (saved)' : 'abcd efgh ijkl mnop'}
                        autoComplete="new-password"
                      />
                    </Field>
                  </div>
                </>
              )}

              {warning && (
                <p className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
                  <ApperIcon name="TriangleAlert" size={16} className="mt-0.5 shrink-0" />
                  {warning}
                </p>
              )}
            </Panel>

            <Panel title="Sender and copies" icon="AtSign" bodyClassName="space-y-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="From address"
                  htmlFor="from-email"
                  hint="Blank uses the username. Gmail only sends as the account or an alias added in Gmail."
                >
                  <Input
                    id="from-email"
                    type="email"
                    value={form.fromEmail}
                    onChange={set('fromEmail')}
                    placeholder={form.username || 'orders@yourdomain.com'}
                  />
                </Field>
                <Field label="From name" htmlFor="from-name">
                  <Input id="from-name" value={form.fromName} onChange={set('fromName')} placeholder="Ghost Cable" />
                </Field>
                <Field label="Replies go to" htmlFor="reply-to" hint={`Blank uses ${s?.defaultOwner ?? 'the owner address'}.`}>
                  <Input
                    id="reply-to"
                    type="email"
                    value={form.replyTo}
                    onChange={set('replyTo')}
                    placeholder={s?.defaultOwner ?? 'admin.ghost.cable@gmail.com'}
                  />
                </Field>
                <Field label="Owner copy of new orders" htmlFor="owner-email" hint="Gets a copy of every new order.">
                  <Input
                    id="owner-email"
                    type="email"
                    value={form.ownerEmail}
                    onChange={set('ownerEmail')}
                    placeholder={s?.defaultOwner ?? 'admin.ghost.cable@gmail.com'}
                  />
                </Field>
              </div>
            </Panel>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <p className="mr-auto text-xs text-muted-foreground">
                Saving checks the SMTP sign-in right away. Settings are stored encrypted and only the owner can change them.
              </p>
              <Button type="submit" disabled={saver.loading}>
                <Busy
                  busy={saver.loading}
                  label={
                    <>
                      <ApperIcon name="Save" size={16} />
                      Save settings
                    </>
                  }
                  busyLabel="Saving and signing in…"
                />
              </Button>
            </div>
          </form>

          <TestPanel configured={!!status.configured} />
        </div>
      )}
    </div>
  );
}
